import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, AlertTriangle, Bot, Clock3, Gauge, Radio, RefreshCw, ShieldCheck, Webhook } from "lucide-react";
import { getOperationsDashboard } from "@/lib/operations/operations.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type Range = "24h" | "7d" | "30d";

function statusBadge(health: string) {
  if (health === "healthy") return <Badge variant="default">Healthy</Badge>;
  if (health === "degraded") return <Badge variant="destructive">Degraded</Badge>;
  return <Badge variant="secondary">No data</Badge>;
}

function formatTime(value: string, range: Range) {
  const date = new Date(value);
  return range === "24h"
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function OperationsDashboard() {
  const [range, setRange] = useState<Range>("7d");
  const load = useServerFn(getOperationsDashboard);
  const query = useQuery({
    queryKey: ["admin", "operations", range],
    queryFn: () => load({ data: { range } }),
    refetchInterval: 60_000,
  });
  const data = query.data;

  return (
    <section id="operations" className="scroll-mt-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase text-cyan">// Production observability</p>
          <h2 className="mt-1 flex items-center gap-2 font-display text-xl"><Activity className="h-5 w-5 text-cyan" /> Operations center</h2>
          <p className="mt-1 text-xs text-muted-foreground">Sensitive runtime telemetry. Visible only to authenticated administrators.</p>
        </div>
        <div className="flex items-center gap-2">
          {(["24h", "7d", "30d"] as Range[]).map((item) => (
            <Button key={item} size="sm" variant={range === item ? "default" : "outline"} onClick={() => setRange(item)}>{item}</Button>
          ))}
          <Button size="icon" variant="outline" aria-label="Refresh operations dashboard" onClick={() => query.refetch()} disabled={query.isFetching}>
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {query.isError && <div className="rounded-md border border-danger/40 bg-danger/10 p-4 text-sm text-danger">Operations telemetry could not be loaded.</div>}
      {!data && !query.isError && <div className="glass rounded-lg p-6 text-sm text-muted-foreground">Loading operational metrics…</div>}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { label: "Quota decisions", value: data.totals.quotaAllowed + data.totals.quotaDenied, sub: `${data.totals.quotaDenied} denied`, icon: Bot },
              { label: "Reservations", value: data.totals.reservationsAllowed, sub: `${data.totals.reservationsLimited} limited`, icon: Radio },
              { label: "Job failures", value: data.totals.jobFailures, sub: `${data.totals.failures} total errors`, icon: Webhook },
              { label: "P95 latency", value: data.totals.p95LatencyMs === null ? "—" : `${data.totals.p95LatencyMs}ms`, sub: `${data.totals.events} events`, icon: Gauge },
            ].map((stat) => (
              <div key={stat.label} className="glass rounded-lg p-4">
                <div className="flex items-center justify-between"><span className="font-mono text-[10px] uppercase text-muted-foreground">{stat.label}</span><stat.icon className="h-4 w-4 text-cyan" /></div>
                <div className="mt-2 font-display text-2xl">{stat.value}</div>
                <div className="mt-1 text-xs text-muted-foreground">{stat.sub}</div>
              </div>
            ))}
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]">
            <div className="glass rounded-lg p-4">
              <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-sm">Event volume</h3><span className="font-mono text-[10px] text-muted-foreground">Auto-refresh 60s</span></div>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.timeline}>
                    <defs>
                      <linearGradient id="operationsTotal" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="var(--cyan)" stopOpacity={0.35}/><stop offset="95%" stopColor="var(--cyan)" stopOpacity={0}/></linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--panel-border)" vertical={false} />
                    <XAxis dataKey="at" tickFormatter={(value) => formatTime(value, range)} tickLine={false} axisLine={false} minTickGap={24} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={28} />
                    <Tooltip labelFormatter={(value) => new Date(String(value)).toLocaleString()} contentStyle={{ background: "var(--background)", borderColor: "var(--panel-border)", borderRadius: 6 }} />
                    <Area type="monotone" dataKey="total" stroke="var(--cyan)" fill="url(#operationsTotal)" strokeWidth={2} />
                    <Area type="monotone" dataKey="failures" stroke="var(--danger)" fill="transparent" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="glass rounded-lg p-4">
              <h3 className="font-display text-sm">Service health</h3>
              <div className="mt-3 divide-y divide-panel-border">
                {data.categorySummary.map((item) => (
                  <div key={item.category} className="flex items-center justify-between gap-3 py-3">
                    <div><div className="text-sm capitalize">{item.category.replace("_", " ")}</div><div className="text-[10px] text-muted-foreground">{item.total} events · {item.failures} failures</div></div>
                    {statusBadge(item.health)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="glass rounded-lg p-4">
            <div className="flex items-center justify-between"><h3 className="flex items-center gap-2 font-display text-sm"><AlertTriangle className="h-4 w-4 text-amber" /> Active alert stream</h3><Badge variant={data.alerts.length ? "destructive" : "default"}>{data.alerts.length}</Badge></div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-xs">
                <thead className="border-b border-panel-border font-mono text-[10px] uppercase text-muted-foreground"><tr><th className="py-2 pr-4">Time</th><th className="py-2 pr-4">Severity</th><th className="py-2 pr-4">Source</th><th className="py-2 pr-4">Outcome</th><th className="py-2">Correlation</th></tr></thead>
                <tbody>
                  {data.alerts.map((event) => <tr key={event.id} className="border-b border-panel-border/50"><td className="py-2 pr-4 text-muted-foreground">{new Date(event.created_at).toLocaleString()}</td><td className="py-2 pr-4"><Badge variant={event.severity === "warning" ? "secondary" : "destructive"}>{event.severity}</Badge></td><td className="py-2 pr-4">{event.category} · {event.operation}</td><td className="py-2 pr-4">{event.outcome}</td><td className="max-w-48 truncate py-2 font-mono text-[10px] text-muted-foreground">{event.correlation_id ?? "—"}</td></tr>)}
                  {!data.alerts.length && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground"><ShieldCheck className="mx-auto mb-2 h-5 w-5 text-cyan" />No warnings or failures in this range.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-muted-foreground"><span className="flex items-center gap-1"><Clock3 className="h-3 w-3" /> Updated {new Date(data.generatedAt).toLocaleString()}</span>{data.truncated && <span>Result capped at 10,000 events; narrow the range for exact totals.</span>}</div>
        </>
      )}
    </section>
  );
}